import { AlertTriangleIcon } from 'lucide-react'

import { Button } from '@/components/ui'
import {
  BANKRUPTCY_SCREENING_STATUSES,
  BANKRUPTCY_STATUSES,
} from '@/modules/tools/bankruptcy/constants'
import { useScreeningLimits } from '@/modules/tools/bankruptcy/hooks'
import type { FilingDetail } from '@/modules/tools/bankruptcy/stores/types'
import { NeedMore } from '../NeedMore'
import { ScreeningProgress } from './ScreeningProgress'
import { ScreeningResults } from './ScreeningResults'

interface ScreeningSectionProps {
  filing: FilingDetail
  isBusy: boolean
  onScreen: () => void
}

export const ScreeningSection = ({
  filing,
  isBusy,
  onScreen,
}: ScreeningSectionProps) => {
  const limits = useScreeningLimits(filing)
  const isScreening = BANKRUPTCY_SCREENING_STATUSES.includes(filing.status)
  const hasDocuments = filing.numberOfPages > 0
  const isScreened = !!filing.screenedAt
  const hasFailed = filing.status === BANKRUPTCY_STATUSES.failed

  const overPageLimit = !!limits?.overPageLimit
  const outOfCredits = !!limits?.outOfCredits
  const blocked = overPageLimit || outOfCredits

  const description = () => {
    if (isScreening) return 'Reading the documents on file now.'
    if (!hasDocuments) return 'Add a document above, then screen the case.'
    if (isScreened) return 'What the last screen found in this case.'
    return 'Screening reads every page on file and fills in the figures below. Re-screening is free.'
  }

  const getAlertMessage = () => {
    if (!hasDocuments) return
    if (overPageLimit) {
      return `This case is over its ${filing.pageLimit}-page limit. Remove a file before screening.`
    }

    if (outOfCredits) {
      const renew = limits?.creditsNextExpiry
        ? ` Your allowance renews ${limits.creditsNextExpiry}.`
        : ''
      return `You're out of screening credits, so this case can't run yet.${renew}`
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-semibold text-lg">Screening results</h2>
          <p className="max-w-lg text-pretty text-muted-foreground text-sm">
            {description()}
          </p>
        </div>
        {!isScreening && (
          <Button
            onClick={onScreen}
            isLoading={isBusy}
            disabled={isBusy || !hasDocuments || blocked}
            loadingText="Starting..."
          >
            {isScreened || hasFailed ? 'Screen again' : 'Screen this case'}
          </Button>
        )}
      </div>

      {getAlertMessage() && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-700 text-sm">
          {getAlertMessage()}{' '}
          <NeedMore limit={overPageLimit ? 'pageCap' : 'allowance'} />
        </p>
      )}

      {hasFailed && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            We couldn't finish reading this case
            {filing.failureReason ? `: ${filing.failureReason}` : ''}. Screen it
            again, and if it keeps failing check that every document is a
            readable PDF.
          </p>
        </div>
      )}

      {isScreening ? (
        <ScreeningProgress pages={filing.numberOfPages} />
      ) : (
        <ScreeningResults filing={filing} />
      )}
    </section>
  )
}
